import { ActionIcon, Menu } from '@mantine/core';
import {
  IconClipboardText,
  IconDotsVertical,
  IconRefresh,
} from '@tabler/icons-react';
import { Fax, FaxStatus } from 'interfaces';
import { MRT_ColumnDef } from 'mantine-react-table';
import { useCurrentSiteStore } from 'store';
import { compareDateAsc, formatDateTime } from 'utils/dateUtils';

interface FaxColumnsProps {
  handleResendFax: (faxId: string) => void;
  handleViewAttachment: (attachmentKey: string) => void;
}

export const FaxColumns = ({
  handleResendFax,
  handleViewAttachment,
}: FaxColumnsProps) => {
  const { timezone } = useCurrentSiteStore();

  const formatCreatedAt = (value: string) =>
    formatDateTime(value, { timezone, fallback: value });

  const columns: MRT_ColumnDef<Fax>[] = [
    {
      id: 'action-menu',
      columnDefType: 'display',
      header: 'Actions',
      size: 10,
      Cell: ({ row }) => {
        const isFailure = row.original.status === FaxStatus.Failed;
        const hasAttachment = !!row.original.attachmentKey;
        const hasActions = isFailure || hasAttachment;

        return (
          <Menu shadow="md" width={180} position="right-start" withinPortal>
            <Menu.Target>
              <ActionIcon
                variant="subtle"
                aria-label="Fax actions"
                onClick={(event) => {
                  event.stopPropagation();
                }}
              >
                <IconDotsVertical size={18} stroke={1.5} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>Actions</Menu.Label>
              {isFailure && (
                <Menu.Item
                  leftSection={<IconRefresh size={14} />}
                  onClick={() => handleResendFax(row.original.id)}
                >
                  Resend Fax
                </Menu.Item>
              )}
              {hasAttachment && (
                <Menu.Item
                  leftSection={<IconClipboardText size={14} />}
                  onClick={() =>
                    handleViewAttachment(row.original.attachmentKey)
                  }
                >
                  View Attachment
                </Menu.Item>
              )}
              {!hasActions && (
                <Menu.Item disabled>No actions available</Menu.Item>
              )}
            </Menu.Dropdown>
          </Menu>
        );
      },
    },
    {
      accessorKey: 'createdAt',
      header: 'Created At',
      sortingFn: (rowA, rowB, columnId) =>
        compareDateAsc(
          rowA.getValue<string>(columnId),
          rowB.getValue<string>(columnId)
        ),
      Cell: ({ cell }) => {
        const value = cell.getValue() as string;
        return formatCreatedAt(value);
      },
    },
    {
      accessorKey: 'recipient',
      header: 'Recipient',
    },
    {
      accessorKey: 'status',
      header: 'Status',
      filterVariant: 'select',
      mantineFilterSelectProps: {
        data: Object.values(FaxStatus).map((status) => ({
          value: status,
          label: status,
        })),
      },
    },
  ];

  return columns;
};
